<template>
  <v-app>
    <v-expand-transition>
      <v-app-bar app class="px-md-6" v-if="showTopNav">
        <div class="d-flex align-center">
          <img
            alt="North Carolina - Teacher Working Condition Survey"
            class="shrink"
            contain
            src="@/assets/nctwcs/logo.png"
            :width="48"
          />
        </div>
        <h1
          class="font-weight-bold primary--text flex-fill text-center text-truncate px-2"
          :class="{ 'text-h4': !isMobile, 'text-h6': isMobile }"
        >
          Teacher Working Condition Survey
        </h1>
        <help-menu color="primary"></help-menu>
      </v-app-bar>
    </v-expand-transition>
    <v-main>
      <v-navigation-drawer
        app
        :value="showSideNav"
        color="primary"
        width="190px"
      >
        <div class="white text-center">
          <img
            alt="North Carolina - Teacher Working Conditions Survey"
            contain
            src="@/assets/nctwcs/logo.png"
            class="pa-4"
            style="width: 100%; max-width: 190px"
          />
        </div>
        <template v-slot:append>
          <v-card-text class="white--text">
            <h6 class="text-h6 font-weight-bold mb-2">Need Help?</h6>
            <p>
              If you need assistance you can contact your district admin.
            </p>
            <!-- <help-block linkClass="white--text" /> -->
          </v-card-text>
        </template>
      </v-navigation-drawer>
      <v-slide-y-transition>
        <div v-if="showForm" class="d-flex h-100">
          <div class="flex-fill pt-6">
            <survey-steps
              class="px-10 py-6"
              v-model="pageIndex"
              :pages="pages"
              :validate="onValidate"
              :formData="formData"
              :loading="loading"
            ></survey-steps>
            <v-scroll-x-transition tag="div" mode="out-in">
              <template v-for="(page, i) in pages">
                <div :key="page.key" v-if="i === pageIndex">
                  <survey-page-title
                    :title="page.displayName"
                    :description="page.description"
                    :pageCount="pages.length"
                    :pageIndex="pageIndex"
                  ></survey-page-title>
                  <v-card-text class="px-md-12 py-md-9 px-6 py-4">
                    <template v-for="question in pageQuestions">
                      <checklist-question
                        ref="pageQuestion"
                        v-if="question.type === questionTypes.checklist"
                        :question="question"
                        :key="question.key"
                        v-model="formData"
                        class="mb-16"
                        :disabled="loading"
                      ></checklist-question>
                      <text-block
                        ref="pageQuestion"
                        v-else-if="question.type === questionTypes.textBlock"
                        :question="question"
                        :key="question.key"
                        class="mb-16"
                      ></text-block>
                      <text-field-question
                        ref="pageQuestion"
                        v-else-if="question.type === questionTypes.text"
                        :question="question"
                        :key="question.key"
                        v-model="formData"
                        class="mb-16"
                        :disabled="loading"
                      ></text-field-question>
                      <table-question
                        ref="pageQuestion"
                        v-else-if="question.type === questionTypes.table"
                        :question="question"
                        :key="question.key"
                        v-model="formData"
                        class="mb-16"
                        :disabled="loading"
                      ></table-question>
                      <radio-list-question
                        ref="pageQuestion"
                        v-if="question.type === questionTypes.radio"
                        :question="question"
                        :key="question.key"
                        v-model="formData"
                        :disabled="loading"
                        class="mb-16"
                      ></radio-list-question>
                      <ranked-choice-question
                        ref="pageQuestion"
                        v-else-if="question.type === questionTypes.rankedChoice"
                        :question="question"
                        :key="question.key"
                        v-model="formData"
                        :disabled="loading"
                        class="mb-16"
                      ></ranked-choice-question>
                    </template>
                  </v-card-text>
                </div>
              </template>
            </v-scroll-x-transition>
            <survey-page-actions
              class="px-md-12 px-6 pb-md-8 pb-6"
              v-model="pageIndex"
              :pages="pages"
              :pageErrors="pageErrors"
              :validate="onValidate"
              :formData="formData"
              :loading="loading"
              @submit="onSave"
            ></survey-page-actions>
          </div>
        </div>
      </v-slide-y-transition>
    </v-main>

    <v-dialog
      :value="!showForm"
      persistent
      :width="code && code.used ? '469' : '300'"
    >
      <v-card>
        <v-card-text v-if="code && code.used" class="text-center">
          <div class="mx-auto py-3 d-inline-flex align-center">
            <img
              alt=""
              aria-hidden="true"
              src="@/assets/nctwcs/party_left.png"
            />
            <div class="font-weight-bold text-no-wrap px-2">
              SURVEY SUBMISSION CONFIRMED!
            </div>
            <img
              alt=""
              aria-hidden="true"
              src="@/assets/nctwcs/party_right.png"
            />
          </div>
          <p>
            Thank you for making time to respond to the 2024 NC Teacher Working
            Conditions Survey because <br/>
            <strong><u>YOUR VOICE MATTERS!!</u></strong>
          </p>
          <img
            alt=""
            aria-hidden="true"
            class="mx-auto d-block"
            src="@/assets/nctwcs/logo_h.png"
          />
          <strong class="d-block mt-2 mb-1">*Please print this page for your records.</strong>
          <v-alert color="secondary lighten-3" class="text-center">
            <div>
              If you are interested in having your name entered into a regional
              drawing for gift cards or other prizes, <br/>
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSf6kVNvqqoHt-UgfTl16WlFpHWhBgZxaJ6H8kOQznIjzqyNhg/viewform"
                target="_blank"
                ><strong>please click here.</strong></a
              >
            </div>
            <div class="text-caption mt-3">
              *Prizes include: gift cards for Bojangles, tickets to NC museums
              and sporting events; wellness packages provided by NCAE and CTA...
              and more!
            </div>
          </v-alert>
        </v-card-text>
        <v-card-text class="pt-6" v-else>
          <img
            alt=""
            aria-hidden="true"
            class="shrink mr-2 d-block m-auto"
            contain
            src="@/assets/nctwcs/logo.png"
            transition="scale-transition"
            width="100%"
          />
          <Validation-Observer
            v-if="!code || !code.used"
            ref="codeObserver"
            tag="form"
            class="mt-8"
            @submit.prevent="onSubmitCode"
          >
            <v-alert
              :value="errorMessage != null"
              class="mb-3"
              color="red"
              type="error"
              border="left"
              transition="slide-y-transition"
              >{{ errorMessage }}
            </v-alert>
            <Validation-Provider rules="required" v-slot="{ errors }">
              <v-text-field
                v-model="formCode"
                placeholder="Enter Your Access Code"
                outlined
                :filled="loading"
                :error-messages="errors"
                :hide-details="errors.length == 0"
                class="text-center"
                :disabled="loading"
              ></v-text-field>
            </Validation-Provider>
            <v-btn
              type="submit"
              block
              color="primary"
              large
              class="mt-3"
              :loading="loading"
            >
              Get Started
            </v-btn>
          </Validation-Observer>
        </v-card-text>
      </v-card>
    </v-dialog>
    <notifications></notifications>
  </v-app>
</template>

<script>
import surveyService from "../../../services/surveyService";

import Notifications from "../../../components/Notifications.vue";

import SurveyPageTitle from "../../../components/Survey.PageTitle.vue";
import SurveySteps from "../../../components/Survey.Steps.vue";
import SurveyPageActions from "../../../components/Survey.PageActions.vue";

import RadioListQuestion from "../../../components/Survey.Question.RadioList.vue";
import RankedChoiceQuestion from "../../../components/Survey.Question.RankedChoice.vue";
import TableQuestion from "../../../components/Survey.Question.Table.vue";

import ChecklistQuestion from "../../../components/Survey.Question.Checklist.vue";
import TextFieldQuestion from "../../../components/Survey.Question.TextField.vue";
import TextBlock from "../../../components/Survey.Question.TextBlock.vue";

import {
  questionTypes,
  showQuestion,
  surveyStatus,
} from "../../../constants/Survey";
// import HelpBlock from "./HelpBlock.vue";
import HelpMenu from "./HelpMenu.vue";
import {
  dtFromServerString,
  formattedTimestamp,
} from "../../../utilities/dataUtilities";

const tenantId = "NC_DPI";

export default {
  name: "SurveyForm",
  components: {
    Notifications,
    SurveyPageTitle,
    SurveySteps,
    SurveyPageActions,
    RadioListQuestion,
    RankedChoiceQuestion,
    TableQuestion,
    // HelpBlock,
    HelpMenu,
    TextBlock,
    TextFieldQuestion,
    ChecklistQuestion,
  },
  head: {
    title: {
      inner: "Teacher Working Conditions Survey - North Carolina",
      complement: " ",
      separator: " ",
    },
  },
  data: () => ({
    pendingStateChanges: 0,
    pageYOffset: 0,
    shrinkThreshold: 70,
    errorMessage: null,
    userCode: "",
    code: null,
    survey: null,
    pageIndex: 0,
    formData: [],
    pageErrors: [],
    started: null,
  }),
  computed: {
    showForm() {
      return this.code && this.code.active && !this.code.used;
    },
    showSideNav() {
      return !this.isMobile && this.showForm === true;
    },
    showTopNav() {
      return this.isMobile && this.showForm === true;
    },
    loading() {
      return this.pendingStateChanges > 0;
    },
    appbarHeight() {
      return this.pageYOffset + 50 < this.shrinkThreshold && !this.isMobile
        ? 148
        : 70;
    },
    logoWidth() {
      return this.pageYOffset + 50 < this.shrinkThreshold && !this.isMobile
        ? 128
        : 48;
    },
    isMobile() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    surveyId() {
      return this.$route.params.id;
    },
    questionTypes() {
      return questionTypes;
    },
    pages() {
      return this.survey ? this.survey.categories : [];
    },
    pageQuestions() {
      let results = [];

      this.pages[this.pageIndex].questions.forEach((x) => {
        if (showQuestion(x, this.formData)) results.push(x);
      });

      return results;
    },
    testing() {
      return this.$route.query.testing == "true";
    },
    formCode: {
      set(val) {
        this.userCode = val.toUpperCase();
      },
      get() {
        return this.userCode;
      },
    },
  },
  watch: {
    pageIndex() {
      this.$vuetify.goTo(document.querySelector("#surveySteps"), {
        duration: 0,
      });
    },
  },
  methods: {
    loadSurvey() {
      this.pendingStateChanges++;

      surveyService
        .get(tenantId, this.surveyId)
        .then(
          (response) => {
            this.survey = response;
          },
          (error) => {
            this.errorMessage =
              error.errorMessage ?? "An unexpected error occurred.";
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    async onSubmitCode() {
      let isValid = await this.$refs.codeObserver.validate();
      if (!isValid) return;

      let startDate = dtFromServerString(this.survey.startDate);
      let endDate = dtFromServerString(this.survey.endDate);
      let now = new Date();

      if (this.survey.status == surveyStatus.published.key) {
        if (now < startDate) {
          this.errorMessage = "The survey hasn't started.";
          return;
        }

        if (now > endDate) {
          this.errorMessage = "The survey has ended.";
          return;
        }
      } else if (this.survey.status == surveyStatus.archived.key) {
        this.errorMessage = "The survey has ended.";
        return;
      } else if (
        this.survey.status == surveyStatus.draft.key &&
        !this.testing
      ) {
        this.errorMessage =
          "The survey is under construction. Please try again later.";
        return;
      } else if (this.survey.status == surveyStatus.draft.key && this.testing) {
        // skip checks.
      } else {
        this.errorMessage =
          "An unexpected error occurred. Please try again later.";
        console.error("Invalid survey status!");
        return;
      }

      this.errorMessage = null;

      this.pendingStateChanges++;
      surveyService
        .getCodeDetail(tenantId, this.surveyId, this.userCode)
        .then(
          (response) => {
            this.code = response;
            this.$nextTick(this.setFormData);
            this.started = new Date();

            if (!this.code.active) 
              this.errorMessage = "Invalid Access Code";
          },
          (error) => {
            console.warn(error);
            if (error.statusCode === 404) {
              this.errorMessage = "Invalid Access Code";
            } else {
              this.errorMessage =
                "An unexpected error occurred. Please try again later.";
            }
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    setFormData() {
      let formData = [];

      this.survey.categories.forEach((cat) =>
        cat.questions.forEach((question) => addFormDataField(question))
      );

      this.formData = formData;

      function addFormDataField(question) {
        switch (question.type) {
          case questionTypes.table:
            question.children.forEach((child) => {
              formData.push({ key: child.key, value: null });
            });
            break;
          default:
            formData.push({ key: question.key, value: null });
            break;
        }
      }
    },
    onValidate() {
      let targets = this.$refs.pageQuestion;
      let focusedFirstErr = false;

      let errors = [];

      if (Array.isArray(targets)) {
        targets.forEach((x) => {
          if (x.errors) errors = errors.concat(x.errors);
          if (x.validate) {
            let isValid = x.validate();
            if (!isValid && !focusedFirstErr) {
              focusedFirstErr = true;

              let scrollPos = Math.floor(window.pageYOffset);
              if (
                scrollPos ==
                document.querySelector("body").offsetHeight - window.innerHeight
              )
                window.scrollTo({
                  top: scrollPos - 1,
                });

              this.$vuetify.goTo(x.$el, {
                duration: document.querySelector("body").offsetHeight * 0.2,
                easing: "easeInOutCubic",
              });
            }
          }
        });
      }

      this.pageErrors = errors;
      return errors.length === 0;
    },
    onSave() {
      if (!this.onValidate()) return;

      let response = {
        accessCode: this.userCode,
        started: formattedTimestamp(this.started),
        completed: formattedTimestamp(Date.now()),
        answers: this.formData.slice().map((x) => ({
          key: x.key,
          value: typeof x.value != "string" ? JSON.stringify(x.value) : x.value,
        })),
        isTest: this.$route.query.testing == "true",
      };

      this.pendingStateChanges++;
      surveyService
        .saveResponse(tenantId, this.surveyId, response)
        .then(
          () => {
            this.code.used = true;
          },
          (error) => {
            console.warn(error);
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred. Please try again later."
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
  },
  created() {
    window.addEventListener("scroll", this.handleScroll);
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  mounted() {
    this.pageYOffset = window.pageYOffset;

    let favicon = document.querySelector("#favicon");
    favicon.href = require("../../../assets/nctwcs/logo.png");

    // Configure App
    this.$store.commit("setTheme", "nctwcs");
    this.$store.commit("setTenantId", tenantId);

    // Load Survey Data
    this.loadSurvey();
    
    for(let key in this.$route.query) {
      if (key.toLowerCase() === 'code') this.userCode = this.$route.query[key];
    }
  },
};
</script>

<style lang="scss">
html {
  font-size: 1rem;
}

#mainNav {
  &,
  .v-toolbar__content,
  img {
    transition: all 200ms ease-in-out !important;
  }
}

.v-icon.v-icon.overlay-back {
  font-size: 24px !important;
}

.v-input {
  &.text-center input {
    text-align: center;
  }

  input:focus::placeholder {
    color: #fff;
  }
}

.h-100 {
  min-height: 100%;
}
</style>
